import React, { useState, useCallback } from 'react'
import { useDispatch } from 'react-redux'
import {
  Box,
  Button,
  CircularProgress,
  Container,
  Grid,
  TextField,
  Typography,
  useTheme,
} from '@mui/material'
import BlockIcon from '@mui/icons-material/Block'
import AllInclusiveIcon from '@mui/icons-material/AllInclusive'
import ConnectWithoutContactIcon from '@mui/icons-material/ConnectWithoutContact'
import Footer from 'components/Layout/Footer'
import { fetchCreateContactEmail } from 'src/redux/contact'
import { validateEmail } from 'data/helpers'

const valueProps = [
  {
    icon: AllInclusiveIcon,
    title: 'Unlimited Everything',
    description:
      'Jobs, seats, candidate conversations. No credits, no caps, no pay-to-promote tiers. One flat annual rate covers your entire recruiting team.',
  },
  {
    icon: BlockIcon,
    title: 'No Placement Fees. Ever.',
    description:
      'Agencies charge 20\u201330% per hire \u2014 that\u2019s $30\u201350K per physician. We charge a flat annual rate. Hire one or hire twenty, the price doesn\u2019t change.',
  },
  {
    icon: ConnectWithoutContactIcon,
    title: 'Direct to Candidates',
    description:
      'Our users joined because they want a job. You\u2019re reaching active job-seekers, not chasing passive profiles. No agencies in the middle.',
  },
]

export default function RequestDemo() {
  const dispatch = useDispatch()
  const theme = useTheme()

  const [values, setValues] = useState({
    firstName: '',
    lastName: '',
    email: '',
    organization: '',
    message: '',
  })
  const [errors, setErrors] = useState({})
  const [loading, setLoading] = useState(false)
  const [submitted, setSubmitted] = useState(false)

  const handleChange = useCallback(
    name => e => {
      const value = e.target.value
      setValues(prev => ({ ...prev, [name]: value }))

      const newErrors = { ...errors }

      if (name === 'email') {
        if (!validateEmail(value)) {
          newErrors.email = 'Invalid email format.'
        } else {
          delete newErrors.email
        }
      }

      if (!value.trim()) {
        newErrors[name] = `${name.charAt(0).toUpperCase() + name.slice(1)} is required.`
      } else if (name !== 'email') {
        delete newErrors[name]
      }

      setErrors(newErrors)
    },
    [errors],
  )

  const handleSubmit = async () => {
    const newErrors = {}
    if (!values.firstName.trim()) newErrors.firstName = 'First name is required.'
    if (!values.lastName.trim()) newErrors.lastName = 'Last name is required.'
    if (!values.email.trim()) newErrors.email = 'Email is required.'
    else if (!validateEmail(values.email)) newErrors.email = 'Invalid email format.'
    if (!values.message.trim()) newErrors.message = 'Message is required.'

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors)
      return
    }

    if (loading) return

    setLoading(true)
    dispatch(fetchCreateContactEmail({
      values: { ...values, subject: 'Team Pricing' },
      onSuccess: () => {
        setSubmitted(true)
        setLoading(false)
      },
    }))
      .finally(() => {
        setLoading(false)
      })
  }

  if (submitted) {
    return (
      <>
        <Box
          sx={{
            backgroundColor: theme.palette.background.dark,
            color: theme.palette.common.white,
            py: { xs: 12, md: 18 },
            textAlign: 'center',
          }}
        >
          <Container maxWidth='sm'>
            <Typography
              variant='h3'
              sx={{
                fontWeight: 700,
                mb: 3,
              }}
            >
              We'll Be in Touch
            </Typography>
            <Typography
              sx={{
                color: 'rgba(255,255,255,0.7)',
                fontSize: '1.15rem',
                lineHeight: 1.7,
                mb: 4,
              }}
            >
              Thanks for reaching out. A member of our team will follow up within one business day
              to learn more about your needs and walk you through the platform.
            </Typography>
          </Container>
        </Box>
        <Footer />
      </>
    )
  }

  return (
    <>
      <Box
        sx={{
          backgroundColor: theme.palette.background.dark,
          color: theme.palette.common.white,
          py: { xs: 8, md: 12 },
        }}
      >
        <Container maxWidth='lg'>
          <Grid container spacing={6} alignItems='center'>
            <Grid item xs={12} md={6}>
              <Typography
                variant='overline'
                sx={{
                  color: theme.palette.primary.light,
                  fontWeight: 600,
                  letterSpacing: '0.15em',
                  fontSize: '0.85rem',
                  display: 'block',
                  mb: 2,
                }}
              >
                Team Plans
              </Typography>
              <Typography
                variant='h3'
                sx={{
                  fontWeight: 700,
                  lineHeight: 1.2,
                  mb: 3,
                }}
              >
                Physician recruiting,{' '}
                <Box
                  component='span'
                  sx={{ color: theme.palette.primary.light }}
                >
                  without the meter running.
                </Box>
              </Typography>
              <Typography
                sx={{
                  color: 'rgba(255,255,255,0.6)',
                  fontSize: '1.1rem',
                  lineHeight: 1.7,
                  mb: 5,
                }}
              >
                Every other platform rations — credits, InMails, placement fees, premium tiers.
                We don't. Unlimited jobs, seats, and direct candidate conversations for a flat
                annual rate. Built by practicing physicians. Priced fair on purpose.
              </Typography>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                {valueProps.map(({ icon: Icon, title, description }) => (
                  <Box key={title} sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                    <Box
                      sx={{
                        backgroundColor: 'rgba(255,255,255,0.08)',
                        borderRadius: '10px',
                        p: 1.2,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}
                    >
                      <Icon sx={{ color: theme.palette.primary.light, fontSize: 24 }} />
                    </Box>
                    <Box>
                      <Typography
                        sx={{
                          fontWeight: 600,
                          fontSize: '1rem',
                          mb: 0.3,
                        }}
                      >
                        {title}
                      </Typography>
                      <Typography
                        sx={{
                          color: 'rgba(255,255,255,0.55)',
                          fontSize: '0.9rem',
                          lineHeight: 1.5,
                        }}
                      >
                        {description}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  backgroundColor: theme.palette.common.white,
                  borderRadius: '12px',
                  p: { xs: 3, md: 4 },
                  boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
                }}
              >
                <Typography
                  variant='h5'
                  sx={{
                    fontWeight: 700,
                    color: theme.palette.text.primary,
                    mb: 0.5,
                  }}
                >
                  Talk to Our Team
                </Typography>
                <Typography
                  sx={{
                    color: theme.palette.text.secondary,
                    fontSize: '0.9rem',
                    mb: 3,
                  }}
                >
                  Tell us about your team. We'll walk you through the platform and put together
                  pricing that fits.
                </Typography>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <TextField
                      fullWidth
                      label='First Name'
                      variant='outlined'
                      size='small'
                      value={values.firstName}
                      onChange={handleChange('firstName')}
                      error={!!errors.firstName}
                      helperText={errors.firstName}
                    />
                    <TextField
                      fullWidth
                      label='Last Name'
                      variant='outlined'
                      size='small'
                      value={values.lastName}
                      onChange={handleChange('lastName')}
                      error={!!errors.lastName}
                      helperText={errors.lastName}
                    />
                  </Box>
                  <TextField
                    fullWidth
                    label='Work Email'
                    variant='outlined'
                    size='small'
                    value={values.email}
                    onChange={handleChange('email')}
                    error={!!errors.email}
                    helperText={errors.email}
                  />
                  <TextField
                    fullWidth
                    label='Organization'
                    variant='outlined'
                    size='small'
                    value={values.organization}
                    onChange={handleChange('organization')}
                  />
                  <TextField
                    fullWidth
                    label='Tell us about your team and recruiting needs'
                    variant='outlined'
                    size='small'
                    multiline
                    rows={4}
                    value={values.message}
                    onChange={handleChange('message')}
                    error={!!errors.message}
                    helperText={errors.message}
                  />
                  <Button
                    fullWidth
                    variant='contained'
                    size='large'
                    onClick={handleSubmit}
                    disabled={loading}
                    sx={{
                      borderRadius: '8px',
                      py: 1.5,
                      fontSize: '1rem',
                      fontWeight: 600,
                      textTransform: 'none',
                      backgroundColor: theme.palette.primary.main,
                      '&:hover': {
                        backgroundColor: theme.palette.primary.dark,
                      },
                    }}
                  >
                    {loading ? <CircularProgress size={24} color='inherit' /> : 'Get Team Pricing'}
                  </Button>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>
      <Footer />
    </>
  )
}
